<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-3xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; Dashboard</a>
        <h1 class="text-2xl font-bold mt-1">Tone Lab</h1>
        <p class="text-emerald-100 text-sm mt-1">Pick any course word or sentence — or type your own — and match the pitch curve.</p>
      </div>
    </header>

    <main class="max-w-3xl mx-auto px-4 py-6 space-y-4">
      <!-- Source tabs + dialect toggle -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:bg-gray-800 dark:border-gray-700">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="selectTab(tab.id)"
            :class="[
              'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors',
              activeTab === tab.id ? 'bg-emerald-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
            ]"
          >
            {{ tab.label }}<span v-if="tab.pro && !subscribed" class="ml-1">🔒</span>
          </button>
        </div>

        <!-- Only rendered when the backend advertises more than one dialect,
             i.e. rake tone_practice:north_audio has been run and committed. -->
        <div v-if="dialects.length > 1" class="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:bg-gray-800 dark:border-gray-700">
          <button
            v-for="d in dialects"
            :key="d.id"
            @click="selectDialect(d.id)"
            :class="[
              'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
              dialect === d.id ? 'bg-emerald-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
            ]"
          >
            {{ d.label }}
          </button>
        </div>
      </div>

      <!-- Course content picker -->
      <div v-if="activeTab === 'course'" class="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700">
        <input
          v-model="search"
          type="search"
          placeholder="Search words — Vietnamese or English…"
          class="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none dark:border-gray-600"
        />
        <p v-if="loadingContent" class="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">Loading course words…</p>
        <p v-else-if="!filteredWords.length" class="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">No words match "{{ search }}".</p>
        <ul v-else class="mt-3 max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          <li v-for="word in filteredWords" :key="word.id">
            <button
              @click="selectWord(word)"
              class="w-full flex items-center gap-3 px-2 py-2.5 text-left rounded-lg transition-colors"
              :class="selected?.id === word.id ? 'bg-emerald-50' : 'hover:bg-gray-50'"
            >
              <span class="font-semibold text-gray-900 shrink-0 dark:text-gray-50">{{ word.text }}</span>
              <span class="text-sm text-gray-500 truncate flex-1 dark:text-gray-400">{{ word.meaning }}</span>
              <span v-if="word.example" class="text-xs text-gray-400 shrink-0 dark:text-gray-500" title="Has an example sentence">💬</span>
              <span class="text-xs text-gray-400 shrink-0 dark:text-gray-500">L{{ word.level }}</span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Random review: a shuffled example sentence from started units -->
      <div v-else-if="activeTab === 'random'" class="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700">
        <p v-if="loadingContent" class="text-center text-sm text-gray-500 dark:text-gray-400">Loading course words…</p>
        <div v-else-if="randomPool.length" class="flex flex-wrap items-center justify-between gap-3">
          <p class="text-sm text-gray-500 dark:text-gray-400">A random sentence from the units you've started — {{ randomPool.length }} to draw from.</p>
          <button
            @click="pickRandom"
            class="font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
          >
            🎲 New sentence
          </button>
        </div>
        <template v-else-if="!startedLevels.length">
          <p class="font-semibold text-gray-900 dark:text-gray-50">Nothing to review yet.</p>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Random review pulls example sentences from units you've started — begin a unit on the dashboard, then come back.</p>
        </template>
        <template v-else>
          <p class="font-semibold text-gray-900 dark:text-gray-50">No sentences to draw from.</p>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">The units you've started don't have practisable example sentences here yet.</p>
        </template>
      </div>

      <!-- Custom text input -->
      <div v-else class="bg-white rounded-xl border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700">
        <template v-if="subscribed">
          <form @submit.prevent="fetchCustomReference" class="flex flex-col sm:flex-row sm:items-start gap-2">
            <VietnameseInput
              v-model="customText"
              wrapperClass="flex-1"
              :maxlength="120"
              placeholder="Type any Vietnamese word or short sentence…"
              :disabled="customState === 'loading'"
              class="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none dark:border-gray-600"
              @enter="fetchCustomReference"
            />
            <button
              type="submit"
              :disabled="customState === 'loading' || !customText.trim()"
              class="font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-5 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap"
            >
              {{ customState === 'loading' ? 'Building reference…' : 'Get reference' }}
            </button>
          </form>
          <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">The reference voice is generated on demand — a sentence can take a little while.</p>
          <p v-if="customError" class="mt-2 text-sm text-red-500">{{ customError }}</p>
        </template>
        <template v-else>
          <p class="font-semibold text-gray-900 dark:text-gray-50">Custom text is a Pro feature.</p>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Type anything — your name, your order at the cà phê stand — and get a reference voice plus your pitch curve against it.</p>
          <a :href="billingUrl" class="mt-3 inline-flex font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-2.5 rounded-lg text-sm transition-colors">Upgrade to Pro</a>
        </template>
      </div>

      <!-- Practice card -->
      <div v-if="target" class="bg-white rounded-xl border border-gray-200 p-6 dark:bg-gray-800 dark:border-gray-700">
        <!-- Word / example toggle for course words -->
        <div v-if="selected?.example" class="flex gap-2">
          <button
            v-for="kind in ['word', 'example']"
            :key="kind"
            @click="selectKind(kind)"
            :class="[
              'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
              currentKind === kind ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            ]"
          >
            {{ kind === 'word' ? 'Word' : 'Full sentence' }}
          </button>
        </div>

        <div class="mt-4 flex items-start gap-4">
          <span :class="target.text.length > 20 ? 'text-2xl' : 'text-4xl'" class="font-extrabold text-gray-900 leading-snug dark:text-gray-50">{{ target.text }}</span>
          <button
            @click="playReference"
            class="grid place-items-center w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xl shadow-sm transition-colors shrink-0"
            aria-label="Hear the reference pronunciation"
          >
            🔊
          </button>
        </div>
        <p v-if="target.meaning" class="mt-1 text-gray-500 text-sm dark:text-gray-400">"{{ target.meaning }}"</p>

        <!-- Recorder — or the daily-quota wall once free practices run out -->
        <div v-if="quotaExhausted" class="mt-6 rounded-xl border-2 border-amber-200 bg-amber-50 p-5 text-center">
          <p class="font-semibold text-gray-900 dark:text-gray-50">That's your {{ quota.limit }} free practices for today.</p>
          <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">You can keep listening to any word — come back tomorrow to record again, or go Pro for unlimited practice.</p>
          <a :href="billingUrl" class="mt-3 inline-flex font-semibold bg-gray-900 text-white hover:bg-gray-800 px-5 py-2.5 rounded-lg text-sm transition-colors">Go Pro</a>
        </div>
        <div v-else class="mt-6 flex flex-col items-center gap-2">
          <button
            @click="toggleRecording"
            :disabled="recState === 'processing'"
            :class="[
              'w-16 h-16 rounded-full flex items-center justify-center text-2xl transition-all',
              recState === 'recording'
                ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
                : recState === 'processing'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            ]"
          >
            {{ recState === 'recording' ? '⏹' : recState === 'processing' ? '⏳' : '🎤' }}
          </button>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ recState === 'recording' ? 'Recording... tap to stop' : recState === 'processing' ? 'Analyzing your pitch...' : 'Listen first, then tap to record yourself' }}
          </p>
          <p v-if="quotaNotice" class="text-xs" :class="quotaNotice.class">
            {{ quotaNotice.text }}
          </p>
          <p v-if="errorMessage" class="text-sm text-red-500">{{ errorMessage }}</p>
        </div>

        <!-- Chart -->
        <div v-if="referenceContour?.length > 1" class="mt-5">
          <ToneContour
            :reference="referenceContour"
            :user="userContour?.length > 1 ? userContour : null"
            :reference-label="`${accent} reference`"
          />
          <p v-if="feedback" class="text-center mt-2 text-sm" :class="{
            'text-emerald-600 font-semibold': feedback.tier === 'great',
            'text-amber-600': feedback.tier === 'close' || feedback.tier === 'keep',
            'text-gray-500': feedback.tier === 'ok',
          }">{{ feedback.headline }} — {{ feedback.text }}</p>
          <p v-else class="text-center mt-2 text-sm text-gray-500 dark:text-gray-400">
            This is the shape to match — tap 🎤 and copy it.
          </p>
        </div>
      </div>

      <p v-else-if="activeTab === 'course' && !loadingContent" class="text-center text-sm text-gray-400 py-4 dark:text-gray-500">
        Pick a word above to see its pitch curve.
      </p>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet } from '../api/client'
import { track } from '../utils/analytics'
import { newAudioRecorder } from '../utils/recording'
import { scoreShape } from '../utils/tone_shape'
import ToneContour from './shared/ToneContour.vue'
import VietnameseInput from './shared/VietnameseInput.vue'

const props = defineProps({
  contentUrl:   { type: String, required: true },
  contourUrl:   { type: String, required: true },
  referenceUrl: { type: String, required: true },
  customUrl:    { type: String, required: true },
  backUrl:      { type: String, required: true },
  billingUrl:   { type: String, required: true },
  subscribed:   { type: Boolean, default: false },
  startedLevels: { type: Array, default: () => [] },
  initialDialect: { type: String, default: 'south' },
})

const tabs = [
  { id: 'course', label: 'Course words' },
  { id: 'random', label: 'Random review' },
  { id: 'custom', label: 'Custom text', pro: true },
]
const activeTab = ref('course')

// Dialect toggle: south (Saigon) / north (Hanoi) when the backend advertises
// both. Starts on the account-wide preference (header/Settings toggle);
// switching here is a per-visit override, not a preference change. `accent`
// names the reference in the scoring copy.
const dialect = ref(props.initialDialect)
const accent = ref('Saigon')
const dialects = ref([])

const loadingContent = ref(true)
const words = ref([])
const search = ref('')
// Which distinct texts this visit actually spent quota on (see the exhaustion
// event). A Set, so five attempts at one word count once.
const scoredTexts = new Set()
const selected = ref(null)       // course word entry
let requestedWordId = Number(new URLSearchParams(window.location.search).get('word')) || null
const currentKind = ref('word')  // word | example (course tab)

// Free-tier daily allowance of scored recordings, from the server; null for
// Pro (limited: false) means "never show quota UI". Updated by every scoring
// response so the counter stays honest across tabs/devices.
const quota = ref(null)

const customText = ref('')
const customState = ref('idle')  // idle | loading
const customError = ref('')
const customReference = ref(null) // { text, points, audio }

const recState = ref('idle')     // idle | recording | processing
const referenceContour = ref(null)
const userContour = ref(null)
const feedback = ref(null)
const errorMessage = ref('')

const subscribed = computed(() => props.subscribed)
const quotaExhausted = computed(() => !!quota.value?.limited && quota.value.remaining <= 0)

// The counter escalates instead of sitting at one volume. A permanently-amber
// "12 of 15 left" is wallpaper by the third recording, and the mic then
// vanishing at zero reads as a bait-and-switch — so it stays neutral while
// there's plenty, warms up on approach, and goes loud for the last couple.
const quotaNotice = computed(() => {
  if (!quota.value?.limited || quota.value.remaining <= 0) return null
  const left = quota.value.remaining
  if (left <= 2) {
    return {
      text: left === 1 ? 'Last free practice today' : `Last ${left} free practices today`,
      class: 'text-red-600 font-semibold'
    }
  }
  const text = `${left} of ${quota.value.limit} free practices left today`
  return left <= 5
    ? { text, class: 'text-amber-600 font-medium' }
    : { text, class: 'text-gray-500' }
})

function setQuota(next) {
  if (!next) return
  const wasExhausted = quotaExhausted.value
  quota.value = next
  if (!wasExhausted && next.limited && next.remaining <= 0) {
    // distinct_texts_attempted separates the two opposite meanings of hitting
    // the wall: exploring several new words (the cap working as an upsell) vs.
    // grinding one word you can't land (the cap breaking the record-see-retry
    // loop the quota exists to protect). Same event, opposite responses.
    track('tone_practice_quota_exhausted', {
      tab: activeTab.value,
      distinct_texts_attempted: scoredTexts.size,
    })
  }
}

// What's on the practice card right now, from either tab.
const target = computed(() => {
  if (activeTab.value === 'custom') {
    return customReference.value
      ? { text: customReference.value.text, meaning: null, audio: customReference.value.audio, level: 1 }
      : null
  }
  if (!selected.value) return null
  const w = selected.value
  return currentKind.value === 'example' && w.example
    ? { text: w.example, meaning: w.example_meaning, audio: w.example_audio, level: w.level }
    : { text: w.text, meaning: w.meaning, audio: w.word_audio, level: w.level }
})

const filteredWords = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return words.value
  return words.value.filter(w =>
    w.text.toLowerCase().includes(q) ||
    w.meaning.toLowerCase().includes(q) ||
    (w.example || '').toLowerCase().includes(q)
  )
})

// Sentences the random-review mode can draw from: an example sentence with
// audio, in a unit the user has started. (Pro words aren't excluded — on
// this surface the daily quota is the gate, not the unit level.)
const randomPool = computed(() =>
  words.value.filter(w => w.example && props.startedLevels.includes(w.level))
)

function selectTab(id) {
  activeTab.value = id
  resetAttempt()
  referenceContour.value = null
  if (id === 'custom' && customReference.value) referenceContour.value = customReference.value.points
  if (id === 'course' && selected.value) loadReference()
  if (id === 'random') pickRandom()
  track('tone_practice_tab_selected', { tab: id })
}

function selectDialect(id) {
  if (dialect.value === id) return
  dialect.value = id
  // Audio, contours and custom references are all per dialect: drop
  // everything derived from them and refetch the word list.
  selected.value = null
  customReference.value = null
  referenceContour.value = null
  resetAttempt()
  loadContent()
  track('tone_practice_dialect_selected', { dialect: id })
}

function pickRandom() {
  const pool = randomPool.value
  if (!pool.length) return
  let word = pool[Math.floor(Math.random() * pool.length)]
  // Re-rolling the same sentence feels broken — nudge to the next one.
  if (pool.length > 1 && word.id === selected.value?.id) {
    word = pool[(pool.indexOf(word) + 1) % pool.length]
  }
  selected.value = word
  currentKind.value = 'example'
  resetAttempt()
  loadReference()
  track('tone_practice_random_selected', { word_id: word.id, level: word.level })
}

function selectWord(word) {
  selected.value = word
  currentKind.value = 'word'
  resetAttempt()
  loadReference()
  track('tone_practice_word_selected', { word_id: word.id, level: word.level })
}

function selectKind(kind) {
  currentKind.value = kind
  resetAttempt()
  loadReference()
  track('tone_practice_kind_selected', { word_id: selected.value?.id, kind })
}

function resetAttempt() {
  userContour.value = null
  feedback.value = null
  errorMessage.value = ''
}

function playReference() {
  if (!target.value?.audio) return
  try {
    new Audio(target.value.audio).play().catch(() => {})
    track('tone_practice_played', { tab: activeTab.value })
  } catch (_e) { /* audio must never break the page */ }
}

// Reference contours come from the tone-practice endpoint, which is ungated:
// looking at any word's curve is part of the free taste. Plain fetch, not the
// api client — an error should show a message, not yank the user elsewhere.
const referenceCache = {}

async function loadReference() {
  referenceContour.value = null
  if (!target.value?.audio) return
  const key = target.value.audio
  if (referenceCache[key]) { referenceContour.value = referenceCache[key]; return }
  try {
    const url = `${props.referenceUrl}?src=${encodeURIComponent(target.value.audio)}`
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
    if (!res.ok) return
    const data = await res.json()
    referenceCache[key] = data?.points || null
    referenceContour.value = referenceCache[key]
  } catch (_e) {
    referenceContour.value = null
  }
}

async function fetchCustomReference() {
  customError.value = ''
  const text = customText.value.trim()
  if (!text) return
  customState.value = 'loading'
  resetAttempt()
  referenceContour.value = null
  try {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const res = await fetch(props.customUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ text, dialect: dialect.value }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      if (res.status === 402) customError.value = 'Custom text needs a Pro subscription.'
      else if (res.status === 429) customError.value = data.error || 'Too many requests — give it a minute.'
      else if (res.status === 422) customError.value = data.error || 'That text didn\'t work — try a shorter phrase.'
      else customError.value = 'The reference voice is unavailable right now. Try again shortly.'
      track('tone_practice_custom_error', { status: res.status })
      return
    }
    customReference.value = await res.json()
    referenceContour.value = customReference.value.points
    track('tone_practice_custom_reference', { length: text.length })
  } catch (_e) {
    customError.value = 'The reference voice is unavailable right now. Try again shortly.'
  } finally {
    customState.value = 'idle'
  }
}

// --- Recorder (same pattern as the public tone trainer) --------------------
let mediaRecorder = null
let audioChunks = []
let autoStopTimer = null

// Longer than the public tool's cap: full example sentences take a few seconds.
const MAX_RECORDING_MS = 12000

function stopRecording() {
  clearTimeout(autoStopTimer)
  if (mediaRecorder?.state === 'recording') mediaRecorder.stop()
  recState.value = 'processing'
}

async function toggleRecording() {
  if (recState.value === 'recording') {
    stopRecording()
    return
  }
  errorMessage.value = ''
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    audioChunks = []
    mediaRecorder = newAudioRecorder(stream) // picks webm or mp4 per browser (iOS Safari has no webm)
    mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data) }
    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop())
      await analyze(new Blob(audioChunks, { type: mediaRecorder.mimeType || 'audio/webm' }))
    }
    mediaRecorder.start()
    recState.value = 'recording'
    autoStopTimer = setTimeout(stopRecording, MAX_RECORDING_MS)
  } catch (_e) {
    errorMessage.value = 'Microphone access is needed — allow it and try again.'
    track('tone_practice_error', { reason: 'mic_denied' })
  }
}

async function analyze(blob) {
  try {
    const formData = new FormData()
    formData.append('audio', blob, blob.type.includes('mp4') ? 'recording.m4a' : 'recording.webm')
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const res = await fetch(props.contourUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrf },
      body: formData,
    })
    if (!res.ok) {
      if (res.status === 429) {
        // Two different 429s: the daily allowance (carries `quota` — swap
        // the mic for the wall) vs the per-minute rate limit (doesn't).
        const data = await res.json().catch(() => ({}))
        if (data.quota) {
          setQuota(data.quota)
        } else {
          errorMessage.value = data.error || 'Too many recordings — try again in a minute.'
          track('tone_practice_error', { reason: 'rate_limited' })
        }
      } else if (res.status === 422) {
        errorMessage.value = "That recording didn't come through — hold the button a beat longer and try again."
        track('tone_practice_error', { reason: 'bad_recording' })
      } else {
        errorMessage.value = 'Pitch analysis is unavailable right now. Try again shortly.'
        track('tone_practice_error', { reason: 'unavailable' })
      }
      return
    }
    const data = await res.json()
    // Record the text BEFORE setQuota, so the exhaustion event fired inside it
    // counts the attempt that hit the wall. Only scoreable contours spend quota
    // (see TonePracticeQuota), which is the same bar used here.
    if (data.points && data.points.length > 1 && target.value?.text) {
      scoredTexts.add(target.value.text)
    }
    setQuota(data.quota)
    if (!data.points || data.points.length < 2) {
      errorMessage.value = "We couldn't hear a voice in that recording — get closer to the mic and try again."
      track('tone_practice_error', { reason: 'no_voice' })
      return
    }
    userContour.value = data.points
    feedback.value = scoreShape(data.points, referenceContour.value, accent.value)
    track('tone_practice_recorded', { tab: activeTab.value, kind: currentKind.value })
  } catch (_e) {
    errorMessage.value = 'Pitch analysis is unavailable right now. Try again shortly.'
    track('tone_practice_error', { reason: 'unavailable' })
  } finally {
    recState.value = 'idle'
  }
}

async function loadContent() {
  loadingContent.value = true
  try {
    let data = await apiGet(`${props.contentUrl}?dialect=${dialect.value}`)
    // An account set to north before the Northern audio is deployed would land
    // on an empty list with the toggle hidden — fall back rather than strand.
    if (dialect.value !== 'south' && !(data.dialects || []).some(d => d.id === dialect.value)) {
      dialect.value = 'south'
      data = await apiGet(`${props.contentUrl}?dialect=south`)
    }
    words.value = data.words || []
    accent.value = data.accent || 'Saigon'
    dialects.value = data.dialects || []
    setQuota(data.quota)
  } catch (_e) {
    words.value = []
  } finally {
    loadingContent.value = false
    // The word detail page links here as /tone-practice?word=<id> ("Practise
    // this tone"). Without this the deep link dropped the learner on an
    // unfiltered picker with nothing selected, so the button appeared to do
    // nothing. Consumed once — a later dialect reload must not yank the
    // learner back to the word they arrived on.
    if (requestedWordId) {
      const match = words.value.find(w => w.id === requestedWordId)
      requestedWordId = null
      if (match) {
        activeTab.value = 'course'
        selectWord(match)
        return
      }
    }
    // Covers landing on (or switching dialect while in) the random tab
    // before the word list has arrived.
    if (activeTab.value === 'random' && !selected.value) pickRandom()
  }
}

onMounted(() => {
  track('tone_practice_started', {})
  loadContent()
})
</script>
